import { Link } from 'react-router-dom';
import PageMetadata from '../components/PageMetadata';
import PublicFooter from '../components/PublicFooter';
import PublicHeader from '../components/PublicHeader';

export default function Landing() {
  return (
    <div className="cv-site">
      <PageMetadata
        title="CounselVault | Controlled Legal-Document Exchange"
        description="CounselVault helps verified correctional facilities approve attorney relationships and securely exchange legal documents with authorized inmates."
        path="/"
      />
      <PublicHeader />
      <main>
        <section className="cv-hero-section">
          <div className="cv-eyebrow">Controlled legal-document exchange</div>
          <h1>Facility-controlled legal document delivery for attorneys and inmates.</h1>
          <p>
            CounselVault gives verified correctional facilities a controlled workflow to approve
            attorneys, authorize attorney-to-inmate relationships, and exchange private legal documents.
          </p>
          <div className="cv-hero-actions" aria-label="Get started">
            <Link to="/request-demo" className="cv-btn cv-btn-primary cv-btn-lg">Request a guided demo</Link>
            <Link to="/register?type=facility" className="cv-btn cv-btn-secondary cv-btn-lg">Facility access request</Link>
            <Link to="/register?type=attorney" className="cv-btn cv-btn-secondary cv-btn-lg">Attorney registration</Link>
            <Link to="/login" className="cv-btn cv-btn-secondary cv-btn-lg">Sign in</Link>
          </div>
          <div className="cv-trust-row" aria-label="Implemented security controls">
            <span>MFA for staff and attorneys</span>
            <span>Facility isolation</span>
            <span>Private storage</span>
            <span>Auditable access</span>
          </div>
        </section>

        <section className="cv-section" aria-labelledby="trust-model-heading">
          <div className="cv-section-heading">
            <span className="cv-kicker">How trust is established</span>
            <h2 id="trust-model-heading">Authority is verified before documents are exchanged.</h2>
            <p>CounselVault verifies the facility. The approved facility controls attorney and inmate relationships.</p>
          </div>
          <ol className="cv-trust-flow">
            {[
              ['01', 'Facility applies', 'An authorized facility representative submits an application.'],
              ['02', 'CounselVault verifies', 'CounselVault independently reviews facility and applicant authority.'],
              ['03', 'Facility approves attorneys', 'The facility approves or rejects attorney access to its environment.'],
              ['04', 'Facility approves relationships', 'The facility authorizes each attorney-to-inmate relationship.'],
              ['05', 'Documents exchanged', 'Authorized users exchange documents through controlled workflows.'],
            ].map(([step, heading, copy]) => (
              <li className="cv-flow-card" key={step}>
                <div className="cv-step">{step}</div>
                <h3>{heading}</h3>
                <p>{copy}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="cv-section cv-dark-panel" aria-labelledby="security-heading">
          <div>
            <span className="cv-kicker">Implemented security boundaries</span>
            <h2 id="security-heading">Private documents, scoped authorization, accountable actions.</h2>
            <p>
              CounselVault combines backend facility isolation, private storage, MFA, session revocation,
              and audit logging. Platform administrators do not impersonate users, and platform support
              does not receive routine access to legal-document contents.
            </p>
            <Link to="/legal/security" className="cv-btn cv-btn-light">Read the Security Policy</Link>
          </div>
          <ul className="cv-security-list">
            <li>MFA-protected administrative and attorney access</li>
            <li>Facility, attorney, inmate, and document authorization</li>
            <li>Private object storage without public document URLs</li>
            <li>Session revocation for sensitive account-state changes</li>
            <li>Audit logging for security-relevant actions</li>
            <li>No platform-admin impersonation workflow</li>
          </ul>
        </section>

        <section className="cv-section" aria-labelledby="audience-heading">
          <div className="cv-section-heading">
            <span className="cv-kicker">Designed around real responsibilities</span>
            <h2 id="audience-heading">Clear controls for facilities and approved attorneys.</h2>
          </div>
          <div className="cv-card-grid">
            <article className="cv-info-card">
              <h3>For correctional facilities</h3>
              <p>Review attorney access, authorize inmate relationships, manage document release, and revoke access when authority changes.</p>
              <Link to="/register?type=facility">Request facility access</Link>
            </article>
            <article className="cv-info-card">
              <h3>For attorneys</h3>
              <p>Create an attorney account, then request facility and client access. Document exchange remains unavailable until the facility approves both relationships.</p>
              <Link to="/register?type=attorney">Register as an attorney</Link>
            </article>
            <article className="cv-info-card">
              <h3>For existing users</h3>
              <p>Sign in to your assigned role and complete any required password or MFA security step.</p>
              <Link to="/login">Sign in to CounselVault</Link>
            </article>
          </div>
        </section>

        <section className="cv-cta" aria-labelledby="cta-heading">
          <h2 id="cta-heading">Start with verified authority.</h2>
          <p>Request access for a facility or register an attorney account to begin the approval process.</p>
          <div className="cv-hero-actions">
            <Link to="/register?type=facility" className="cv-btn cv-btn-primary cv-btn-lg">Facility access request</Link>
            <Link to="/register?type=attorney" className="cv-btn cv-btn-secondary cv-btn-lg">Attorney registration</Link>
          </div>
        </section>
      </main>
      <PublicFooter />
    </div>
  );
}
